/**
 * Stale-deploy recovery for code-split chunks.
 *
 * Every frontend deploy replaces /assets with new content-hashed files. A tab
 * still running the previous build (opened before the deploy, or booted from
 * a cached index.html) then asks for chunk names that no longer exist, and the
 * dynamic import rejects with "Failed to fetch dynamically imported module".
 * Reloading picks up the current index.html and the chunks that match it.
 *
 * A short sessionStorage guard stops a reload loop when the chunk is genuinely
 * missing on the server rather than just stale in this tab.
 */

const RELOAD_KEY = 'neuro:chunk-reload-at';
const RELOAD_GUARD_MS = 30_000;

// Chrome / Firefox / Safari wordings for a failed dynamic import, Vite's CSS
// preload failure, and webpack-style ChunkLoadError messages.
const CHUNK_ERROR_RE =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS|Loading (?:CSS )?chunk [\w-]+ failed/i;

let reloading = false;

export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  if ((error as { name?: unknown }).name === 'ChunkLoadError') return true;
  const message = typeof error === 'string' ? error : (error as { message?: unknown }).message;
  return typeof message === 'string' && CHUNK_ERROR_RE.test(message);
}

/**
 * Reload the page if `error` is a chunk-load failure and we haven't just
 * reloaded for one. Returns true when a reload is under way, so callers can
 * skip crash reporting and error UI.
 */
export function reloadForChunkLoadError(error: unknown): boolean {
  if (!isChunkLoadError(error)) return false;
  if (reloading) return true;

  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
    if (Date.now() - last < RELOAD_GUARD_MS) return false;
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    // Without sessionStorage we can't tell a stale tab from a broken deploy,
    // so don't risk a reload loop.
    return false;
  }

  reloading = true;
  window.location.reload();
  return true;
}

/** Test-only: forget that a reload was triggered in this page. */
export function __resetChunkLoadRecoveryForTests(): void {
  reloading = false;
}
